import type { FC, ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useSSEConnectionContext } from '@/infra/sse'

const DEFAULT_UPDATE_INTERVAL = 1000

interface UptimeContextValue {
  readonly uptime: number
  readonly isTracking: boolean
}

interface UptimeProviderProps {
  readonly children: ReactNode
}

const UptimeContext = createContext<UptimeContextValue | null>(null)

export const UptimeProvider: FC<UptimeProviderProps> = ({ children }) => {
  const sseContext = useSSEConnectionContext()
  const [uptime, setUptime] = useState(0)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const startTimeRef = useRef<number | null>(null)

  const isTracking = startTimeRef.current !== null

  const clearInterval_ = useCallback((): void => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const updateUptime = useCallback((): void => {
    if (startTimeRef.current === null) return
    setUptime(Date.now() - startTimeRef.current)
  }, [])

  useEffect(() => {
    if (sseContext.isConnected && startTimeRef.current === null) {
      startTimeRef.current = Date.now()
      setUptime(0)
    } else if (!sseContext.isConnected && startTimeRef.current !== null) {
      startTimeRef.current = null
      setUptime(0)
    }
  }, [sseContext.isConnected])

  useEffect(() => {
    if (startTimeRef.current === null) {
      clearInterval_()
      return
    }

    updateUptime()
    intervalRef.current = setInterval(updateUptime, DEFAULT_UPDATE_INTERVAL)

    return clearInterval_
  }, [updateUptime, sseContext.isConnected, clearInterval_])

  const value = useMemo<UptimeContextValue>(
    () => ({
      uptime,
      isTracking,
    }),
    [uptime, isTracking],
  )

  return <UptimeContext.Provider value={value}>{children}</UptimeContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useUptimeContext(): UptimeContextValue {
  const context = useContext(UptimeContext)
  if (!context) {
    throw new Error('useUptimeContext must be used within UptimeProvider')
  }
  return context
}
